"use client";

import Script from "next/script";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: Record<string, unknown>,
      ) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

export interface TurnstileWidgetHandle {
  reset: () => void;
}

interface TurnstileWidgetProps {
  action: string;
  onTokenChange: (token: string) => void;
  onTokenClear: () => void;
}

const TURNSTILE_SCRIPT_ID = "cloudflare-turnstile";

const TurnstileWidget = forwardRef<TurnstileWidgetHandle, TurnstileWidgetProps>(
  function TurnstileWidget({ action, onTokenChange, onTokenClear }, ref) {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const widgetIdRef = useRef<string | null>(null);
    const onTokenChangeRef = useRef(onTokenChange);
    const onTokenClearRef = useRef(onTokenClear);
    const [scriptReady, setScriptReady] = useState(false);

    const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

    useEffect(() => {
      onTokenChangeRef.current = onTokenChange;
      onTokenClearRef.current = onTokenClear;
    }, [onTokenChange, onTokenClear]);

    useImperativeHandle(ref, () => ({
      reset() {
        onTokenClearRef.current();

        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.reset(widgetIdRef.current);
        }
      },
    }));

    useEffect(() => {
      if (
        !siteKey ||
        !scriptReady ||
        !window.turnstile ||
        !containerRef.current
      ) {
        return;
      }

      if (widgetIdRef.current) {
        return;
      }

      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        theme: "light",
        action,
        callback: (token: string) => onTokenChangeRef.current(token),
        "error-callback": () => onTokenClearRef.current(),
        "expired-callback": () => onTokenClearRef.current(),
        "timeout-callback": () => onTokenClearRef.current(),
      });

      return () => {
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.remove(widgetIdRef.current);
          widgetIdRef.current = null;
        }
      };
    }, [action, scriptReady, siteKey]);

    if (!siteKey) {
      return (
        <p className="turnstile-widget__error">
          Verification is temporarily unavailable. Please try again later.
        </p>
      );
    }

    return (
      <>
        <Script
          id={TURNSTILE_SCRIPT_ID}
          src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
          strategy="afterInteractive"
          onLoad={() => setScriptReady(true)}
          onReady={() => setScriptReady(true)}
        />

        <div className="turnstile-widget" ref={containerRef} />
      </>
    );
  },
);

export default TurnstileWidget;
